/**
 * LaunchpadListPage
 *
 * Lists all launches owned by the authenticated user.
 * Allows creating new launches and viewing/editing existing ones.
 *
 * Accessible via: /launchpad (not in nav bar yet)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { DefaultPageLayout } from '../ui/layouts/DefaultPageLayout';
import { Button } from '../ui/components/Button';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { useAuthStore } from '../store/authStore';
import {
  getLaunches,
  type LaunchListItem,
  type LaunchStatus,
  getStatusDisplay,
  formatMintPrice,
} from '../api/launches';
import { FeatherPlus, FeatherMonitor } from '@subframe/core';

export const LaunchpadListPage: React.FC = () => {
  const { isAuthenticated, activeWalletAddress } = useAuthStore();

  const [launches, setLaunches] = useState<LaunchListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchLaunches = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      setLoading(true);
      setError(null);
      const response = await getLaunches();
      setLaunches(response.launches);
    } catch (err) {
      console.error('Failed to load launches:', err);
      setError(err instanceof Error ? err.message : 'Failed to load launches');
      // Clear launches on error to prevent showing stale data from a different wallet
      setLaunches([]);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, activeWalletAddress]);

  useEffect(() => {
    fetchLaunches();
  }, [fetchLaunches]);

  // Show login prompt if not authenticated
  if (!isAuthenticated) {
    return (
      <DefaultPageLayout>
        <div className="container max-w-4xl mx-auto px-4 py-12">
          <div className="flex flex-col items-center justify-center text-center gap-4">
            <h1 className="text-heading-2 font-heading-2 text-default-font">
              My Launches
            </h1>
            <p className="text-body font-body text-subtext-color max-w-md">
              Connect your wallet to view and manage your launches.
            </p>
          </div>
        </div>
      </DefaultPageLayout>
    );
  }

  const getStatusBadgeClasses = (status: LaunchStatus): string => {
    const { color } = getStatusDisplay(status);
    const baseClasses = 'px-2 py-1 rounded-full text-caption font-caption';

    switch (color) {
      case 'green':
        return `${baseClasses} bg-success-100 text-success-700`;
      case 'yellow':
        return `${baseClasses} bg-warning-100 text-warning-700`;
      case 'blue':
        return `${baseClasses} bg-brand-100 text-brand-700`;
      case 'red':
        return `${baseClasses} bg-error-100 text-error-700`;
      case 'gray':
      default:
        return `${baseClasses} bg-neutral-100 text-neutral-700`;
    }
  };

  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <DefaultPageLayout>
      <div className="container max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-4">
              <h1 className="text-heading-1 font-heading-1 text-default-font">
                My Launches
              </h1>
              <Link to="/launchpad/guide" className="text-caption font-caption text-brand-600 hover:underline">
                Creator Guide
              </Link>
            </div>
            <p className="text-body font-body text-subtext-color mt-1">
              Create and manage your NFT launches
            </p>
          </div>
          <div className="hidden sm:flex items-center gap-2">
            <Link to="/mint">
              <Button variant="neutral-secondary" icon={<FeatherPlus />}>Mint NFT</Button>
            </Link>
            <Link to="/launchpad/new">
              <Button icon={<FeatherPlus />}>Launch New Collection</Button>
            </Link>
          </div>
        </div>

        {/* Mobile Desktop Notice */}
        <div className="sm:hidden mb-6 p-4 bg-brand-50 dark:bg-brand-950 border border-brand-200 dark:border-brand-800 rounded-lg">
          <div className="flex items-start gap-3">
            <FeatherMonitor className="w-5 h-5 text-brand-600 dark:text-brand-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-body-bold font-body-bold text-brand-700 dark:text-brand-300">
                Desktop recommended
              </p>
              <p className="text-caption font-caption text-brand-600 dark:text-brand-400 mt-1">
                Creating and managing collections works best on a desktop browser. You can view your collections here on mobile.
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <LoadingSpinner size="md" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-16 gap-4">
            <p className="text-body font-body text-error-600">{error}</p>
            <Button variant="neutral-secondary" onClick={fetchLaunches}>
              Try Again
            </Button>
          </div>
        ) : launches.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 sm:py-16 gap-4 bg-default-background rounded-lg border border-neutral-border px-4">
            <h2 className="text-heading-3 font-heading-3 text-default-font text-center">
              No Collections Yet
            </h2>
            <p className="text-body font-body text-subtext-color max-w-md text-center">
              Ready to bring your NFT collection to life? Launch your first collection and start minting.
            </p>
            <Link to="/launchpad/new" className="hidden sm:block">
              <Button icon={<FeatherPlus />}>Launch Your First Collection</Button>
            </Link>
            <p className="sm:hidden text-caption font-caption text-subtext-color text-center">
              Visit this page on desktop to create your first collection.
            </p>
          </div>
        ) : (
          <div className="grid gap-4">
            {launches.map((launch) => {
              const statusDisplay = getStatusDisplay(launch.status);

              return (
                <Link
                  key={launch.id}
                  to={`/launchpad/${launch.id}`}
                  className="flex items-start sm:items-center gap-3 sm:gap-4 p-3 sm:p-4 bg-default-background rounded-lg border border-neutral-border hover:border-brand-500 transition-colors"
                >
                  {/* Thumbnail */}
                  <div className="w-14 h-14 sm:w-16 sm:h-16 flex-shrink-0 bg-neutral-100 dark:bg-neutral-800 rounded-lg overflow-hidden">
                    {launch.thumbnail_url ? (
                      <img
                        src={launch.thumbnail_url}
                        alt={launch.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="text-heading-3 font-heading-3 text-neutral-400">
                          {launch.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <h3 className="text-body-bold font-body-bold text-default-font truncate max-w-[120px] sm:max-w-none">
                        {launch.name}
                      </h3>
                      <span className={getStatusBadgeClasses(launch.status)}>
                        {statusDisplay.label}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-caption font-caption text-subtext-color">
                      <span>/{launch.slug}</span>
                      <span>
                        {launch.total_items} item{launch.total_items !== 1 ? 's' : ''}
                      </span>
                      <span>{formatMintPrice(launch.mint_price_drops)}</span>
                      <span className="hidden sm:inline">Created {formatDate(launch.created_at)}</span>
                    </div>
                  </div>

                  {/* Progress (for active launches) */}
                  {launch.status === 'active' && launch.total_supply && (
                    <div className="hidden md:flex flex-col items-end gap-1">
                      <span className="text-caption font-caption text-subtext-color">
                        Minted
                      </span>
                      <span className="text-body-bold font-body-bold text-default-font">
                        {launch.minted_count} / {launch.total_supply}
                      </span>
                      <div className="w-24 h-2 bg-neutral-100 dark:bg-neutral-800 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-brand-500 transition-all"
                          style={{
                            width: `${Math.round(
                              (launch.minted_count / launch.total_supply) * 100
                            )}%`,
                          }}
                        />
                      </div>
                    </div>
                  )}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </DefaultPageLayout>
  );
};
